/**
 * StreakCounter — animated streak badge showing consecutive days completed.
 *
 * Displays a flame emoji + streak number (e.g., "🔥 14").
 * Milestone styling at 7, 14, 30, 60, 90 day marks.
 * If streak === 0, renders faded "0" state.
 *
 * @see Requirements 8.3, 8.4
 */

import { useMemo } from 'react';

export interface StreakCounterProps {
  /** Current streak count (consecutive days). */
  streak: number;
  /** Optional color override for the badge. */
  color?: string;
}

/** Streak milestone thresholds that receive extra visual emphasis. */
const MILESTONES = [7, 14, 30, 60, 90];

/**
 * Determines if a streak value is at a milestone threshold.
 */
function isMilestone(streak: number): boolean {
  return MILESTONES.includes(streak);
}

export function StreakCounter({ streak, color }: StreakCounterProps) {
  const milestone = useMemo(() => isMilestone(streak), [streak]);

  if (streak === 0) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-xs text-text-secondary opacity-40"
        aria-label="No current streak"
      >
        <span aria-hidden="true">🔥</span>
        <span>0</span>
      </span>
    );
  }

  return (
    <span
      className={[
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5',
        'text-xs font-semibold',
        'transition-all duration-default ease-default',
        'streak-counter-animate',
        milestone
          ? 'bg-primary-light text-primary shadow-card scale-110'
          : 'text-text-secondary',
      ].join(' ')}
      style={color ? { color } : undefined}
      aria-label={`${streak} day streak${milestone ? ', milestone reached' : ''}`}
    >
      <span aria-hidden="true" className={milestone ? 'animate-pulse' : ''}>
        🔥
      </span>
      <span className="streak-number">{streak}</span>
    </span>
  );
}

StreakCounter.displayName = 'StreakCounter';
